import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { getAddons } from "@/server/api/get-addons.get";
import { getProducts } from "@/server/api/get-products.get";
import type { AddonType } from "@/struct/AddonsStruct";
import { TariffArray } from "@/struct/TariffStruct";

export function useAddonsPage() {
  const store = useStore();
  const { t } = useI18n();

  const availableAddons = ref<TariffArray[]>([]);
  const isAddonDetails = ref(false);
  const isAddAddon = ref(false);
  const isUnsuccessful = ref(false);
  const isSuccessful = ref(false);
  const isUnhandledError = ref(false);
  const isIncompatible = ref(false);
  const isAlreadyAdded = ref(false);
  const isLoading = ref(false);
  const chosenAddon = ref<AddonType>();
  const isSticky = ref(false);

  const mainTariff = computed(() => store.getters["tariff/mainTariff"]);
  const configDataBrand = computed(
    () => store.getters["brand/configDataBrand"]
  );

  const showDetails = (addon: AddonType) => {
    chosenAddon.value = addon;
    isAddonDetails.value = true;
  };

  const startAddonAction = async (addon: AddonType) => {
    isAddAddon.value = true;
    chosenAddon.value = addon;
  };

  const addAddon = async (id: number) => {
    await store
      .dispatch("product/addProduct", id)
      .then((res) => {
        isSuccessful.value = true;
      })
      .catch((err) => {
        switch (err.response.data.code) {
          case 57:
            isIncompatible.value = true;
            break;
          case 58:
            isAlreadyAdded.value = true;
            break;
          case 62:
            isUnsuccessful.value = true;
            break;
          default:
            isUnhandledError.value = true;
        }
      })
      .finally(() => {
        isAddAddon.value = false;
      });
  };
  const closeAddModal = () => {
    isAddAddon.value = false;
  };

  const closeAllModal = () => {
    isAddAddon.value = false;
    isUnsuccessful.value = false;
    isSuccessful.value = false;
    isUnhandledError.value = false;
    isIncompatible.value = false;
    isAlreadyAdded.value = false;
  };

  const computeAddonTitle = (item: AddonType) => {
    const activationPrice = Math.floor(parseInt(item.activation_price));
    if (activationPrice > 0) {
      return t("addons.modals.connectPaidTitle", {
        name: item.name,
        price: activationPrice,
      });
    }
    return t("addons.modals.connectFreeTitle", { name: item.name });
  };

  const toPayment = () => {
    store.dispatch("togglePaymentModal", true);
  };

  const handleScroll = () => {
    isSticky.value = window.scrollY > 120;
  };

  const updateFullData = async () => {
    if (!mainTariff.value) return;
    isLoading.value = true;
    window.addEventListener("scroll", handleScroll);
    let includedAddons: never[] = [];
    await getProducts(
      mainTariff.value.subscriptionId,
      mainTariff.value.compatibleId,
      mainTariff.value.facility
    )
      .then((res) => {
        includedAddons = res.data.response;
        console.log(includedAddons);
      })
      .catch((err) => {
        console.log(err);
      });
    await getAddons()
      .then((res) => {
        if (!res.data?.data) return;
        const allAddons: TariffArray[] = res.data.data.filter(
          (a: TariffArray) => a.publish
        );
        const enabledAddons =
          store.state.tariff.subscriptionDetails.additionalProducts;

        const enabledIds = new Set(enabledAddons.map((p: any) => p.productId));
        const filteredAddons = allAddons.filter(
          (addon: any) => !enabledIds.has(addon.ems_product_id)
        );

        if (includedAddons.length) {
          const includedIds = new Set(includedAddons.map((p: any) => p.id));

          availableAddons.value = filteredAddons.filter(
            (addon: any) =>
              includedIds.has(addon.ems_product_id) &&
              addon.available_to_activate === 1
          );
        } else {
          availableAddons.value = filteredAddons;
        }
      })
      .catch((err) => {
        console.error("Failed to get addons:", err);
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const pageTitle = computed(() =>
    isAddonDetails.value
      ? t("addons.page.availableOptions")
      : t("addons.page.addOption")
  );

  watch(
    () => mainTariff.value,
    (newData) => {
      if (newData) updateFullData();
    }
  );

  onMounted(() => {
    updateFullData();
  });

  onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
  });

  return {
    addAddon,
    availableAddons,
    chosenAddon,
    closeAddModal,
    closeAllModal,
    computeAddonTitle,
    isAddonDetails,
    isAddAddon,
    isAlreadyAdded,
    isIncompatible,
    isLoading,
    isSticky,
    isSuccessful,
    isUnhandledError,
    isUnsuccessful,
    pageTitle,
    configDataBrand,
    showDetails,
    startAddonAction,
    toPayment,
  };
}
